import { LitElement } from "lit";

import { customElement, property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { html } from "lit/static-html.js";

import { buttonStyles } from "./button.css";

@customElement("wow-button")
export class WowButton extends LitElement {
  static readonly formAssociated = true;
  private readonly internals: ElementInternals;

  static override styles = [buttonStyles];

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.internals.role = "button";

    this.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();

        if (this.internals.form) {
          this.internals.form.requestSubmit();
        }
      }
    });
  }

  @property({ type: String }) override title = "";
  @property({ type: String, reflect: true })
  variant: "primary" | "neutral" | "subtle" | "danger" = "primary";
  @property({ type: String, reflect: true })
  size: "large" | "medium" | "small" = "medium";
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) loading = false;
  @property({ type: String, reflect: true }) type: "submit" | "button" | "reset" = "button";

  @property({ type: Boolean, reflect: true, attribute: "full-width" }) fullWidth = false;
  @property({ type: Boolean, reflect: true, attribute: "mobile-full-width" }) mobileFullWidth = false;
  @property({ type: Boolean, reflect: true }) rounded = false;
  @property({ type: Boolean, reflect: true }) square = false;
  @property({ type: String }) color?: string;
  @property({ type: String }) backgroundColor?: string;
  @property({ type: String }) borderColor?: string;

  @property({ type: String, reflect: true }) href = "";
  @property({ type: String }) target?: "_blank" | "_self" | "_parent" | "_top";
  @property({ type: String }) rel = "noreferrer noopener";
  @property({ type: String }) download?: string;

  @property({ type: Number, reflect: true }) override tabIndex: number = 0;

  override connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener("click", this.#onClick);
    this.setAttribute("role", "button");
  }

  formDisabledCallback(disabled: boolean): void {
    this.disabled = disabled;
  }

  #onClick = (event: Event): void => {
    if (this.disabled) {
      event.preventDefault();
      return;
    }

    if (this.href) return;

    const explicitFormId = this.getAttribute("form");
    const root = this.getRootNode() as ShadowRoot | Document;
    const formViaAttr = explicitFormId ? (root.getElementById(explicitFormId) as HTMLFormElement | null) : null;
    const form = this.internals.form ?? formViaAttr ?? this.closest("form");

    if (!form) return;

    switch (this.type) {
      case "submit":
        form.requestSubmit();
        break;
      case "reset":
        form.reset();
        break;
    }
  };

  renderButton() {
    const customStyle = [
      this.backgroundColor
        ? `--custom-bg-color: ${this.backgroundColor}; --custom-bg-color-hover: color-mix(in srgb, ${this.backgroundColor} 90%, black);`
        : "",
      this.color ? `--custom-color: ${this.color};` : "",
      this.borderColor ? `--custom-border-color: ${this.borderColor};` : "",
    ].join(" ");

    return html`
      <button
        class=${classMap({
          button: true,
          [this.variant]: true,
          [this.size]: true,
          "full-width": this.fullWidth,
          rounded: this.rounded,
          square: this.square, // sets aspect ratio 1/1
          "custom-color": !!this.color,
          "custom-bg": !!this.backgroundColor,
          "custom-border": !!this.borderColor,
          "mobile-full-width": this.mobileFullWidth,
        })}
        style=${customStyle}
        ?disabled=${this.disabled}
        type=${ifDefined(this.type)}
        title=${ifDefined(this.title)}
        tabindex="-1"
        aria-disabled=${this.disabled ? "true" : "false"}
      >
        <slot name="prefix" part="prefix" class="button__prefix"></slot>
        <slot part="label" class="button__label"></slot>
        <slot name="suffix" part="suffix" class="button__suffix"></slot>
      </button>
    `;
  }

  renderAnchor() {
    const customStyle = this.backgroundColor
      ? `--custom-bg-color: ${this.backgroundColor}; --custom-bg-color-hover: color-mix(in srgb, ${this.backgroundColor} 90%, black);`
      : "";

    return html`
      <a
        class=${classMap({
          button: true,
          [this.variant]: true,
          [this.size]: true,
          "full-width": this.fullWidth,
          rounded: this.rounded,
          "custom-bg": !!this.backgroundColor,
        })}
        style=${customStyle}
        href=${this.href}
        target=${ifDefined(this.target)}
        download=${ifDefined(this.download)}
        rel=${ifDefined(this.rel)}
        tabindex=${this.disabled ? "-1" : "0"}
      >
        <slot name="prefix" part="prefix" class="button__prefix"></slot>
        <slot part="label" class="button__label"></slot>
        <slot name="suffix" part="suffix" class="button__suffix"></slot>
      </a>
    `;
  }

  override render() {
    return this.href ? this.renderAnchor() : this.renderButton();
  }
}
